import { percent } from '@affine/core/utils';
import { FeatureName, type QuotaQuery } from '@affine/graphql';
import { isBackendError, isNetworkError } from '@docnboard/rest';
import { cssVar } from '@docnboard/theme';
import {
  backoffRetry,
  catchErrorInto,
  effect,
  Entity,
  exhaustMapSwitchUntilChanged,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
} from '@toeverything/infra';
import bytes from 'bytes';
import { EMPTY, map, mergeMap } from 'rxjs';

import type { AuthService } from '../services/auth';
import type { UserQuotaStore } from '../stores/user-quota';

const color = (percent: number) =>
  percent > 80 ? cssVar('errorColor') : cssVar('processingColor');

export class UserQuota extends Entity {
  quota$ = new LiveData<NonNullable<QuotaQuery['currentUser']>['quota']>(null);
  used$ = new LiveData<{
    usedStorageSize: number;
    usedWorkspaceCount: number;
  } | null>(null);

  usedFormatted$ = this.used$.map(used =>
    used !== null
      ? {
          usedStorageSize: bytes.format(used.usedStorageSize),
          usedWorkspaceCount: used.usedWorkspaceCount,
        }
      : null
  );
  max$ = this.quota$.map(quota =>
    quota
      ? {
          storageQuota: quota.storageQuota,
          workspaceQuota:
            quota.name === FeatureName.unlimited
              ? Infinity
              : quota.workspaceLimit,
        }
      : null
  );
  maxFormatted$ = this.max$.map(max =>
    max
      ? {
          storageQuota: bytes.format(max.storageQuota),
          workspaceQuota:
            max.workspaceQuota === Infinity
              ? '∞'
              : max.workspaceQuota.toString(),
        }
      : null
  );

  percent$ = LiveData.computed(get => {
    const max = get(this.max$);
    const used = get(this.used$);
    if (max === null || used === null) {
      return null;
    }
    return {
      storageQuota: percent(used.usedStorageSize, max.storageQuota),
      workspaceQuota:
        max.workspaceQuota === Infinity
          ? 0 // 0% для безлимитного плана
          : percent(used.usedWorkspaceCount, max.workspaceQuota),
    };
  });

  color$ = this.percent$.map(percent =>
    percent !== null
      ? {
          storageQuota: color(percent.storageQuota),
          workspaceQuota: color(percent.workspaceQuota),
        }
      : null
  );

  isLoading$ = new LiveData(false);
  isRevalidating$ = new LiveData(false);
  error$ = new LiveData<any | null>(null);

  constructor(
    private readonly authService: AuthService,
    private readonly store: UserQuotaStore
  ) {
    super();
  }

  revalidate = effect(
    map(() => ({ accountId: this.authService.session.account$.value?.id })),
    exhaustMapSwitchUntilChanged(
      (a, b) => a.accountId === b.accountId,
      ({ accountId }) =>
        fromPromise(async signal => {
          if (!accountId) {
            return; // no quota if no user
          }
          const { quota, usedStorageSize, usedWorkspaceCount } =
            await this.store.fetchUserQuota(signal);

          return { quota, usedStorageSize, usedWorkspaceCount };
        }).pipe(
          backoffRetry({ when: isNetworkError, count: Infinity }),
          backoffRetry({ when: isBackendError }),
          mergeMap(data => {
            if (data) {
              const { quota, usedStorageSize, usedWorkspaceCount } = data;
              this.quota$.next(quota);
              this.used$.next({ usedStorageSize, usedWorkspaceCount });
            } else {
              this.quota$.next(null);
              this.used$.next(null);
            }
            return EMPTY;
          }),
          catchErrorInto(this.error$),
          onStart(() => {
            this.isRevalidating$.next(true);
            this.isLoading$.next(true);
          }),
          onComplete(() => {
            this.isRevalidating$.next(false);
            this.isLoading$.next(false);
          })
        ),
      () => {
        // Reset the state when the user is changed
        this.reset();
      }
    )
  );

  reset() {
    this.quota$.next(null);
    this.used$.next(null);
    this.error$.next(null);
    this.isRevalidating$.next(false);
  }

  override dispose(): void {
    this.revalidate.unsubscribe();
  }
}
